import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import api from "@/lib/api";
import { STORAGE_KEYS } from "@/lib/storage";
import { logDev } from "@/lib/logger";

const AuthContext = createContext(null);

function readStoredUser() {
  try {
    const raw = localStorage.getItem(STORAGE_KEYS.user);
    return raw ? JSON.parse(raw) : null;
  } catch (err) { logDev("readStoredUser failed", err); return null; }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(readStoredUser);
  const [loading, setLoading] = useState(true);

  // On mount, validate the httpOnly cookie session via /auth/me.
  // The browser sends the cookie automatically (withCredentials on axios).
  useEffect(() => {
    let cancelled = false;
    api.get("/auth/me").then(({ data }) => {
      if (cancelled) return;
      setUser(data);
      localStorage.setItem(STORAGE_KEYS.user, JSON.stringify(data));
    }).catch(() => {
      if (cancelled) return;
      localStorage.removeItem(STORAGE_KEYS.user);
      setUser(null);
    }).finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const login = useCallback(async (email, password) => {
    // Backend sets the httpOnly `access_token` cookie on the response.
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem(STORAGE_KEYS.user, JSON.stringify(data.user));
    setUser(data.user);
    return data.user;
  }, []);

  const signup = useCallback(async (payload) => {
    const { data } = await api.post("/auth/signup", payload);
    localStorage.setItem(STORAGE_KEYS.user, JSON.stringify(data.user));
    setUser(data.user);
    return data.user;
  }, []);

  const logout = useCallback(async () => {
    try { await api.post("/auth/logout"); } catch (err) { logDev("auth logout (clearing local anyway)", err); }
    localStorage.removeItem(STORAGE_KEYS.user);
    setUser(null);
  }, []);

  const value = useMemo(
    () => ({ user, login, signup, logout, loading }),
    [user, login, signup, logout, loading]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
